<template>
  <b-container class="px-4 py-5 custom-container">
    <div class="page-title pt-5 pb-3 mt-5">My Account</div>
    <b-card>
      <div class="menu-container mx-auto pb-5 w-100">
        <!-- Small screens: the tab labels don't fit horizontally, so swap the
             tab strip for a dropdown selector (the tab nav is hidden via CSS). -->
        <div class="tab-select-wrap mb-3">
          <b-form-select
            :value="tabIndex"
            :options="tabSelectOptions"
            class="tab-select font-weight-bold"
            @change="onSelectTab"
          />
        </div>
        <b-tabs
          v-model="tabIndex"
          content-class="mt-3"
          nav-class="font-weight-bold"
        >
          <b-tab
            v-if="userVerified || userIsBusinessAdminWithoutToken"
            title="Profile"
            title-link-class="nav-title"
            class="mr-5"
            @click="onClickTabByHash('#profile')"
          >
            <div v-if="!isProfileReady" class="text-center py-5">
              <digger-loader />
            </div>
            <profile-form
              v-else
              :readOnly="userIsBusinessAdminWithoutToken"
              :is-primary-business-admin="isPrimaryBusinessAdmin"
            />
          </b-tab>
          <!-- <b-tab
            title="Payments"
            title-link-class="nav-title"
            @click="onClickTabByHash(1)"
          >
            <payment-list />
          </b-tab>
          <b-tab
            title="Payouts"
            title-link-class="nav-title"
            @click="onClickTabByHash(2)"
          >
            <payout-details />
          </b-tab> -->
          <b-tab
            v-if="!userIsBusinessAdminWithoutToken"
            title="Account"
            title-link-class="nav-title"
            class="mr-5"
            @click="onClickTabByHash('#account')"
          >
            <account-form :hide-reminders="isProjectShareBaOnTrial" />
            <privacy-consent-options v-if="userIsTradie" />
            <account-options v-if="userVerified" />
          </b-tab>
          <b-tab
            v-else-if="userIsBusinessAdminWithoutToken"
            title="Account"
            title-link-class="nav-title"
            class="mr-5"
            @click="onClickTabByHash('#account')"
          >
            <account-form />
          </b-tab>

          <b-tab
            v-if="canManageUsers"
            title="Users"
            title-link-class="nav-title"
            class="mr-5"
            @click="onClickTabByHash('#business-admins')"
          >
            <company-form :is-primary-business-admin="isPrimaryBusinessAdmin" />
          </b-tab>

          <b-tab
            v-if="canViewLogs"
            title="Logs"
            title-link-class="nav-title"
            class="mr-5"
            @click="onClickTabByHash('#logs')"
          >
            <log-form />
          </b-tab>

          <b-tab
            v-if="canViewRoles"
            title-link-class="nav-title"
            class="mr-5"
            @click="onClickTabByHash('#roles')"
          >
            <template #title
              >Roles <span class="beta-tag">(Beta)</span></template
            >
            <roles-manager />
          </b-tab>

          <b-tab
            v-if="canViewRoles"
            title-link-class="nav-title"
            class="mr-5"
            @click="onClickTabByHash('#permissions')"
          >
            <template #title>
              Permissions <span class="beta-tag">(Beta)</span>
            </template>
            <permissions-catalogue />
          </b-tab>
        </b-tabs>
      </div>
    </b-card>

    <b-modal
      v-if="!userIsBusinessAdminWithoutToken && !isCreatedViaProjectShareBa"
      v-model="showUploadAvatar"
      centered
      hide-header-close
      hide-footer
      no-close-on-esc
      no-close-on-backdrop
      title="Upload Profile Photo"
    >
      <b-overlay :show="isLoading" rounded="sm">
        <template #overlay>
          <div class="text-center">
            <digger-loader />
          </div>
        </template>
        <b-form @submit.prevent="onSubmit" enctype="multipart/form-data">
          <!-- Profile Avatar Label -->
          <label class="font-weight-bold">
            Photo of yourself for identification & verification purposes.
            <b-icon
              icon="info-circle-fill"
              variant="primary"
              @click="onBtnProfilePhotoInfoClicked"
              title="Check Profile Photo Guideline"
              scale="2"
              class="cursor-pointer pb-2"
            />
          </label>

          <!-- Profile Avatar and Image Cropper -->
          <ImageCropper
            v-model="showImageCropper"
            @change="onImageCropFinished"
            @close="showImageCropper = false"
          />
          <div
            class="upload-container my-2 mx-auto"
            :class="form.avatar ? 'border-0' : ''"
            @drop.prevent="onProfilePhotoChanged"
            @dragover.prevent
            v-cloak
            :style="{
              width: '280px',
              height: '280px',
              border:
                state.avatar === false
                  ? '4px solid #DC3545 !important'
                  : 'none',
            }"
          >
            <b-avatar
              button
              rounded
              badge
              badge-top
              badge-offset="0.8rem"
              alt="Avatar"
              class="ml-0 mr-auto edit-avatar"
              variant="transparent"
              badge-variant="transparent"
              :style="'max-width: 100%; max-height: 100%; width: 100%; height: auto;'"
              :src="form.avatar.url"
              @click="showImageCropper = true"
            >
              <template #badge>
                <b-icon
                  icon="pencil-square"
                  scale="1.5"
                  class="text-light"
                ></b-icon>
              </template>
            </b-avatar>
          </div>
          <b-form-invalid-feedback
            v-if="!!errors.avatar"
            :state="state.avatar"
            class="text-center mb-2"
          >
            {{ errors.avatar[0] }}
          </b-form-invalid-feedback>
          <div v-else class="mb-4"></div>

          <b-button type="submit" variant="primary" class="mb-2" block>
            Submit Photo
          </b-button>
        </b-form>
      </b-overlay>
    </b-modal>

    <!-- User Verification Reminder Modal -->
    <b-modal
      v-if="!userIsBusinessAdminWithoutToken"
      v-model="showVerificationReminder"
      centered
      ok-only
    >
      <template #modal-title>
        <h3 class="font-weight-bold">Account Verification</h3>
      </template>

      <p style="opacity: 1; font-size: 20px">
        Please verify your Email and Phone Number <br />
        by clicking on the
        <span style="font-weight: bold; color: red">Verify</span>
        links provided
      </p>
      <p
        v-if="userIsTradie"
        style="opacity: 1; font-size: 17px"
        class="mt-3 mb-0"
      >
        Check the original email for the code.
      </p>
    </b-modal>

    <!-- Business Admin without token reminder -->
    <b-modal
      v-if="userIsBusinessAdminWithoutToken"
      v-model="showBaNoTokenReminder"
      centered
      ok-only
    >
      <template #modal-title>
        <h3 class="font-weight-bold">Access Locked</h3>
      </template>

      <p style="opacity: 1; font-size: 20px">
        Your access is limited because no access token has been assigned to you.
        <br /><br />
        You can still verify your Email and Phone Number in the
        <span style="font-weight: bold">Account</span> tab. <br /><br />
        Please contact your company’s primary Business Admin and ask them to
        assign a token to your account so you can use all TradiePass features.
      </p>
    </b-modal>
  </b-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import ProfileForm from '@/components/forms/ProfileForm'
// import PaymentList from '@/components/lists/PaymentList'
// import PayoutDetails from '@/components/cards/PayoutDetails'
import AccountForm from '@/components/forms/AccountForm'
import CompanyForm from '@/components/forms/CompanyForm'
import LogForm from '@/components/forms/LogForm'
import AccountOptions from '@/components/cards/AccountOptions'
import PrivacyConsentOptions from '@/components/cards/PrivacyConsentOptions'
import ImageCropper from '@/components/modals/ImageCropper'
import RolesManager from '@/components/roles/RolesManager'
import PermissionsCatalogue from '@/components/roles/PermissionsCatalogue'

/** Matches User::CREATED_VIA_PROJECT_SHARE_BA (API `created_via`). */
const CREATED_VIA_PROJECT_SHARE_BA = 'project_share_ba'

export default {
  components: {
    ProfileForm,
    // PaymentList,
    // PayoutDetails,
    AccountForm,
    CompanyForm,
    LogForm,
    AccountOptions,
    PrivacyConsentOptions,
    ImageCropper,
    RolesManager,
    PermissionsCatalogue,
  },

  data() {
    return {
      tabIndex: 0,
      showUploadAvatar: false,
      showVerificationReminder: false,
      showBaNoTokenReminder: false,
      isPrimaryBusinessAdmin: null,
      isProfileReady: false,

      form: {
        avatar: {
          image_data: null,
          url: 'https://placehold.co/160x160?text=Click+to+add+photo',
        },
      },

      state: {
        avatar: null,
      },

      errors: {
        avatar: null,
      },

      showImageCropper: false,
      isLoading: false,
      isAvatarPassedAICheck: null,
      avatarAIErrorMessage: null,

      /** User chose a tab via click; skip init-time sync so we don't fight b-tabs. */
      userChoseTab: false,
      /** True while applying hash from onClickTab — ignore $route.hash watcher (same click). */
      skipHashSyncFromClick: false,
    }
  },

  watch: {
    'form.avatar.image_data'() {
      this.state.avatar = !!this.form.avatar && this.isAvatarPassedAICheck
    },
    '$route.hash'() {
      if (this.skipHashSyncFromClick) return
      this.$nextTick(() => this.syncTabFromRoute())
    },
  },

  computed: {
    ...mapGetters('auth', ['user', 'isProjectScoped', 'hasPermission']),
    ...mapState({
      company: (state) => state.company.company,
    }),
    /** Tab order in the template (Payments/Payouts removed; indices must match b-tabs v-model). */
    visibleHashes() {
      const hashes = []
      if (this.userVerified || this.userIsBusinessAdminWithoutToken) {
        hashes.push('#profile')
      }
      hashes.push('#account')
      if (this.canManageUsers) hashes.push('#business-admins')
      if (this.canViewLogs) hashes.push('#logs')
      if (this.canViewRoles) {
        hashes.push('#roles')
        hashes.push('#permissions')
      }
      return hashes
    },
    /**
     * Options for the small-screen tab dropdown. Kept in lock-step with
     * `visibleHashes` (same order) so the select's value maps 1:1 to the
     * active b-tabs index.
     */
    tabSelectOptions() {
      const labels = {
        '#profile': 'Profile',
        '#account': 'Account',
        '#business-admins': 'Users',
        '#logs': 'Logs',
        '#roles': 'Roles (Beta)',
        '#permissions': 'Permissions (Beta)',
      }
      return this.visibleHashes.map((hash, index) => ({
        value: index,
        text: labels[hash] || hash,
      }))
    },
    /**
     * Gates the Users tab (business admins and tradie supervisors).
     */
    canManageUsers() {
      return (
        !this.userIsTradie &&
        !this.userIsBusinessAdminWithoutToken &&
        this.userVerified &&
        (this.hasPermission('settings.users.view') ||
          this.hasPermission('settings.users.manage'))
      )
    },
    /** Gates the Logs tab. */
    canViewLogs() {
      return (
        !this.userIsTradie &&
        !this.userIsBusinessAdminWithoutToken &&
        this.userVerified &&
        this.hasPermission('settings.logs.view')
      )
    },
    /**
     * Gates the Roles and Permissions tabs. True when the user holds any of
     * settings.roles.view, settings.roles.create, or settings.roles.manage.
     * Edit/delete actions within the tab are further gated in RolesManager.
     */
    canViewRoles() {
      return (
        !this.userIsTradie &&
        !this.userIsBusinessAdminWithoutToken &&
        this.userVerified &&
        (this.hasPermission('settings.roles.view') ||
          this.hasPermission('settings.roles.create') ||
          this.hasPermission('settings.roles.manage'))
      )
    },
    userVerified() {
      return this.user.email_verified && this.user.phone_number_verified
    },
    userHasNoAvatar() {
      return this.user.avatar === null
    },
    isCreatedViaProjectShareBa() {
      return this.user && this.user.created_via === CREATED_VIA_PROJECT_SHARE_BA
    },
    /**
     * Project-share BA still inside their 30-day free trial — `profile_completion_due_at`
     * hasn't passed yet. The Account tab is shown, but the Reminders section is hidden
     * until the trial ends.
     */
    isProjectShareBaOnTrial() {
      if (!this.isCreatedViaProjectShareBa) return false
      if (!this.user.profile_completion_due_at) return false
      const due = new Date(this.user.profile_completion_due_at).getTime()
      if (Number.isNaN(due)) return false
      return due > Date.now()
    },
    userIsTradie() {
      return this.user && this.user.user_type === 'tradie'
    },
    userIsBusinessAdminWithoutToken() {
      return (
        this.user &&
        this.user.user_type === 'business_admin' &&
        !this.user.has_token &&
        !this.user.is_free &&
        !this.isPrimaryBusinessAdmin &&
        !this.isProjectScoped
      )
    },
  },

  methods: {
    ...mapActions({
      getAuthenticatedUser: 'auth/getAuthenticatedUser',
      getCompany: 'company/getCompany',
    }),

    onClickTabByHash(hash) {
      this.userChoseTab = true
      this.skipHashSyncFromClick = true
      const finishClickNav = () => {
        this.$nextTick(() => {
          this.skipHashSyncFromClick = false
        })
      }
      if (this.userIsBusinessAdminWithoutToken) {
        const allowed = ['#profile', '#account']
        const safeHash = allowed.includes(hash) ? hash : '#profile'
        this.$router
          .push({ name: 'user.settings-page', hash: safeHash })
          .then(finishClickNav)
          .catch(finishClickNav)
        return
      }
      this.$router
        .push({ name: 'user.settings-page', hash })
        .then(finishClickNav)
        .catch(finishClickNav)
    },

    /** Small-screen dropdown chose a tab: mirror a tab click (sets index + route hash). */
    onSelectTab(index) {
      this.tabIndex = index
      const hash = this.visibleHashes[index]
      if (hash) this.onClickTabByHash(hash)
    },

    /** Map route hash to b-tabs index (0..n-1); HASHNAMES slots 1–2 are legacy (Payments/Payouts). */
    hashToDomIndex(hash) {
      const hashes = this.visibleHashes
      const defaultHash = hashes[0] || '#account'
      let h = (hash || '').trim() || defaultHash
      if (h === '#payments' || h === '#payouts') h = '#account'

      let idx = hashes.indexOf(h)
      if (idx !== -1) return idx

      if (this.userIsBusinessAdminWithoutToken) {
        const allowed = ['#profile', '#account']
        const fallback = allowed.includes(h) ? h : '#profile'
        idx = hashes.indexOf(fallback)
        return idx >= 0 ? idx : 0
      }

      return 0
    },

    syncTabFromRoute() {
      if (this.userIsBusinessAdminWithoutToken) {
        const requestedHash = this.$route.hash || '#profile'
        const allowed = ['#profile', '#account']
        const initialHash = allowed.includes(requestedHash)
          ? requestedHash
          : '#profile'
        this.tabIndex = this.hashToDomIndex(initialHash)
        return
      }
      this.tabIndex = this.hashToDomIndex(this.$route.hash)
    },

    onBtnProfilePhotoInfoClicked() {
      alert('hey')
    },

    async onImageCropFinished(image) {
      const data = new FormData()
      data.append('avatar', image)

      await this.$api
        .post('process-avatar', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(({ data }) => {
          const passedImage = this.processImageFromAPI(atob(data.data))
          this.isAvatarPassedAICheck = true
          this.errors.avatar = null
          // Only swap the preview once the photo has actually passed the
          // face-detection check — on failure, leave whatever was showing
          // before (the placeholder, or the current avatar) in place
          // instead of the rejected shot.
          this.form.avatar.image_data = passedImage
          this.form.avatar.url = URL.createObjectURL(passedImage)
        })
        .catch((error) => {
          this.isAvatarPassedAICheck = false
          this.avatarAIErrorMessage = error.response.data.message
          this.errors.avatar = [this.avatarAIErrorMessage]
          // The image_data watcher drives state.avatar, but it won't fire
          // since we didn't touch image_data above — set it directly so the
          // red border / invalid-feedback message still appear.
          this.state.avatar = false
        })
        .finally(() => {
          this.showImageCropper = false
        })
    },

    processImageFromAPI(byteString) {
      const uint8Array = new Uint8Array(byteString.length)
      for (let i = 0; i < byteString.length; i++)
        uint8Array[i] = byteString.charCodeAt(i)
      return new Blob([uint8Array], { type: 'image/jpeg' })
    },

    async onSubmit() {
      this.isLoading = true

      const formData = new FormData()
      const form = this.form

      Object.keys(form).forEach((key) => {
        if (key === 'avatar') {
          if (form.avatar.image_data !== null && this.isAvatarPassedAICheck)
            formData.append('avatar', form.avatar.image_data)
        } else {
          formData.append(key, form[key])
        }
      })

      await this.$api
        .post('auth/profile/avatar', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(async () => {
          await this.getAuthenticatedUser()
          this.showUploadAvatar = false
        })
        .catch((error) => {
          this.errors = error.response.data.errors

          if (this.isAvatarPassedAICheck === false)
            this.errors.avatar = [this.avatarAIErrorMessage]

          Object.entries(this.errors).forEach(
            ([key, value]) => (this.state[key] = false),
          )
        })
        .finally(() => (this.isLoading = false))
    },

    async initialiseSettings() {
      const isBa = this.user && this.user.user_type === 'business_admin'

      // ── Fast-path: resolve BA status without an API call ──────────────────
      if (isBa) {
        if (typeof this.user.is_primary === 'boolean') {
          // Backend already includes is_primary on the user object.
          this.isPrimaryBusinessAdmin = this.user.is_primary
        } else {
          // Check sessionStorage cache (keyed per user so it's safe).
          const cacheKey = `isPrimary_${this.user.id}`
          const cached = sessionStorage.getItem(cacheKey)
          if (cached !== null) {
            this.isPrimaryBusinessAdmin = cached === 'true'
          }
        }
      }

      // ── Determine what still needs fetching asynchronously ────────────────
      const needsBaFetch = isBa && this.isPrimaryBusinessAdmin === null
      const needsCompanyFetch =
        this.user && this.user.company_id && !this.company

      if (!needsBaFetch && !needsCompanyFetch) {
        // Everything is already available — render immediately, no spinner.
        this.isProfileReady = true
      } else {
        const promises = []

        if (needsBaFetch) {
          promises.push(
            (async () => {
              try {
                const { data } = await this.$api.get(
                  'company/business-admins',
                  {
                    params: { search: '', archived_only: false },
                  },
                )
                const items = data.data || data
                const match = items.find(
                  (item) =>
                    (item.user_id && item.user_id === this.user.id) ||
                    item.id === this.user.id,
                )
                this.isPrimaryBusinessAdmin = !!(match && match.is_primary)
                // Cache for the rest of the session so repeat visits are instant.
                try {
                  sessionStorage.setItem(
                    `isPrimary_${this.user.id}`,
                    String(this.isPrimaryBusinessAdmin),
                  )
                } catch (e) {}
              } catch (e) {
                this.isPrimaryBusinessAdmin = false
              }
            })(),
          )
        }

        if (needsCompanyFetch) {
          promises.push(
            (async () => {
              try {
                await this.getCompany(this.user.company_id)
              } catch (e) {}
            })(),
          )
        }

        await Promise.all(promises)
        this.isProfileReady = true
      }

      if (this.userIsBusinessAdminWithoutToken) {
        this.showBaNoTokenReminder = true

        // One-time notification per session (invited BA without token).
        try {
          const key = 'baNoTokenNoticeShown'
          if (sessionStorage.getItem(key) !== '1') {
            sessionStorage.setItem(key, '1')
            this.$bvToast.toast(
              'Business Admin accounts must be assigned a token by the primary Business Admin before full access is available.',
              {
                title: 'Token Required',
                variant: 'warning',
                autoHideDelay: 8000,
                solid: true,
              },
            )
          }
        } catch (e) {}
      } else {
        // Personal-photo modal is a tradie-only prompt; BAs upload a company
        // logo during onboarding and should not be asked for a personal photo.
        if (!isBa && this.userHasNoAvatar) this.showUploadAvatar = true
        else if (!this.userVerified) this.showVerificationReminder = true
      }

      // Business admin whose role grants no permissions — the router parked them
      // here. Explain why (once per session).
      if (this.$route.query && this.$route.query.no_access === '1') {
        try {
          const key = 'baNoAccessNoticeShown'
          if (sessionStorage.getItem(key) !== '1') {
            sessionStorage.setItem(key, '1')
            this.$bvToast.toast(
              'Your role does not grant access to any sections yet. Please ask your primary Business Admin to assign permissions to your role.',
              {
                title: 'No Access Granted',
                variant: 'warning',
                autoHideDelay: 8000,
                solid: true,
              },
            )
          }
        } catch (e) {}
      }

      // Don't overwrite the active tab after the user already clicked (avoids Profile ↔ Users flicker
      // when is_primary loads and visible tabs change). Still enforce BA-without-token rules.
      if (this.userIsBusinessAdminWithoutToken || !this.userChoseTab) {
        this.syncTabFromRoute()
      }
    },
  },

  created() {
    this.initialiseSettings()
  },
}
</script>

<style lang="scss">
.nav-title {
  padding: 10px 0;
  margin-right: 75px;
  color: grey;
  text-align: left;
  white-space: nowrap;

  &:hover,
  &:focus {
    color: grey;
  }

  // Same small "(Beta)" tag treatment as the main navbar tabs.
  .beta-tag {
    font-size: 0.7em;
    opacity: 0.8;
  }
}
.nav-tabs {
  border-bottom: none;

  .nav-link:hover {
    border-color: white;
  }
  .nav-link.active,
  .nav-item.show .nav-link {
    color: #2c66d5;
    border: none;
    border-bottom: 4px solid #2c66d5;
    border-radius: 0;
  }
}
.menu-container {
  padding: 0 85px;
}

// ── Responsive tab spacing ──────────────────────────────────────────────
// The 75px tab gap + 85px container padding are tuned for a full-size laptop.
// As the viewport shrinks we scale both down so the tabs stay tidy; below
// ~700px the labels no longer fit on one line, so the tab strip is hidden and
// a dropdown selector (.tab-select-wrap) takes over instead.
@media (max-width: 1200px) {
  .menu-container {
    padding: 0 20px;
  }
  .nav-title {
    margin-right: 45px;
  }
}
@media (max-width: 768px) {
  .menu-container {
    padding: 0 16px;
  }
  .nav-title {
    margin-right: 28px;
  }
}
@media (max-width: 575.98px) {
  // Trim the outer page padding (the grey margin outside the white card) so the
  // white card — and its content — uses more of the screen width.
  .custom-container {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
  // Bootstrap's default .card-body padding is 1.25 rem each side — at 320 px
  // that wastes 40 px. Reduce to 0.75 rem on each side equally so the card
  // content stays centred with balanced left/right space.
  .custom-container > .card > .card-body {
    padding-left: 0.75rem !important;
    padding-right: 0.75rem !important;
  }
  // Match the card-body reduction: drop from 8 px to 4 px each side so the
  // inner content stays symmetrically indented.
  .menu-container {
    padding-left: 4px;
    padding-right: 4px;
  }
}

// Below ~700px the tab strip no longer fits on one line (the beta-tagged
// Roles/Permissions labels wrap onto a second row), so it's hidden and the
// dropdown selector takes over.
@media (max-width: 699.98px) {
  .menu-container .nav-tabs {
    display: none;
  }
}

.tab-select-wrap {
  display: none;
  max-width: 320px;

  @media (max-width: 699.98px) {
    display: block;
  }
}
.tab-select.custom-select {
  height: auto;
  padding: 0.6rem 2.25rem 0.6rem 1rem;
  color: #2c66d5;
  font-size: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background-color: #f8fafc;
  box-shadow: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;

  &:hover {
    border-color: #c7d2e5;
  }

  &:focus {
    border-color: #2c66d5;
    box-shadow: 0 0 0 3px rgba(44, 102, 213, 0.15);
  }
}
</style>
